import { supabase } from '../lib/supabase';
import { ENV } from '../config/env';
import { storage } from '../lib/storage';

export interface LogoItem {
  id: string;
  url: string;
  prompt: string;
  storageKey?: string | null;
  signedUrl?: string | null;
}

interface IdeogramImage {
  prompt: string;
  resolution: string;
  is_image_safe?: boolean;
  seed?: number;
  url?: string;
  image_url?: string;
  image?: string;
  style_type?: string;
}

interface IdeogramApiResponse {
  created: string;
  data: IdeogramImage[];
  success?: boolean;
  warning?: string;
  error?: string;
}

interface LegacyApiResponse {
  logos: LogoItem[];
  success?: boolean;
  warning?: string;
  error?: string;
}

type ApiResponse = IdeogramApiResponse | LegacyApiResponse;

async function getAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return (data?.session as any)?.access_token || null;
  } catch (e) {
    console.error('logoService.getAccessToken error:', e);
    return null;
  }
}

async function getUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const userId = (data?.session as any)?.user?.id || null;
    return userId;
  } catch (e) {
    console.error('logoService.getUserId error:', e);
    return null;
  }
}

export const logoService = {
  async generate(params: { prompt: string; size: number }): Promise<{ logos: LogoItem[]; raw?: IdeogramApiResponse; warning?: string }>{
    // Auth: send Supabase access token via Authorization header (verifyToken middleware expects it)
    const token = await getAccessToken();
    const userId = await getUserId();
    if (!token || !userId) throw new Error('Authentication required');

    const resp = await fetch(`${ENV.API_URL}/api/marketing/logo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        // Some environments still require userId in the body; include it for compatibility
        userId,
        prompt: params.prompt,
        size: params.size,
      }),
    });

    let data: ApiResponse;
    try {
      const contentType = resp.headers.get('content-type') || '';
      if (!resp.ok) {
        // Try to parse error JSON if possible, otherwise include text snippet
        if (contentType.includes('application/json')) {
          const errJson = await resp.json().catch(() => null);
          const message = (errJson as any)?.error || `Logo API error (${resp.status})`;
          throw new Error(message);
        } else {
          const text = await resp.text().catch(() => '');
          const snippet = text ? ` body: ${text.slice(0, 200)}` : '';
          throw new Error(`Logo API error (${resp.status}). Non-JSON response${snippet}`);
        }
      }
      if (!contentType.includes('application/json')) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Unexpected response format from logo service. Expected JSON but received '${contentType}'. Snippet: ${text.slice(0, 200)}`);
      }
      data = await resp.json();
    } catch (e) {
      console.error('logoService.generate JSON parse error:', e);
      throw new Error('Invalid response from logo service');
    }

    // Ideogram official format
    if ('data' in data && Array.isArray((data as IdeogramApiResponse).data) && (data as IdeogramApiResponse).data.length > 0) {
      const ideogram = data as IdeogramApiResponse;
      const baseTimestamp = Date.now();
      const logos: LogoItem[] = ideogram.data
        .map((image, index) => {
          const url = image.url || (image as any).image_url || (image as any).image;
          if (!url) return null;
          return {
            id: `logo-${baseTimestamp}-${index}`,
            url,
            prompt: image.prompt || params.prompt,
          } as LogoItem;
        })
        .filter(Boolean) as LogoItem[];
      if (logos.length === 0) {
        throw new Error('Logo service returned images without URLs');
      }

      const storedLogos = await Promise.all(
        logos.map(async (logo, index) => {
          try {
            const response = await fetch(logo.url, { mode: 'cors' });
            if (!response.ok) throw new Error(`Failed to download logo asset (${response.status})`);
            const blob = await response.blob();
            const mimeType = blob.type || 'image/png';
            const extension = mimeType.split('/')[1] || 'png';
            const fileName = `logo-${baseTimestamp}-${index}.${extension}`;
            const file = new File([blob], fileName, { type: mimeType });
            const path = `logo-generator/${userId}/${fileName}`;
            const upload = await storage.uploadFile({
              file,
              path,
              metadata: {
                sourceModule: 'logo-generator',
                docType: 'image',
                visibility: 'private',
                tags: ['logo', params.prompt].filter(Boolean),
                prompt: params.prompt,
              },
            });
            const signedUrl = await storage.getSignedUrl(upload.key, 600);
            return { ...logo, storageKey: upload.key, signedUrl };
          } catch (assetErr) {
            console.error('Failed to persist logo to storage:', assetErr);
            return { ...logo, storageKey: null, signedUrl: logo.url };
          }
        })
      );

      return { logos: storedLogos, raw: ideogram, warning: (data as any).warning };
    }

    // Legacy format
    if ('logos' in data && Array.isArray((data as LegacyApiResponse).logos) && (data as LegacyApiResponse).logos.length > 0) {
      const legacy = data as LegacyApiResponse;
      return { logos: legacy.logos, warning: legacy.warning };
    }

    const errMsg = (data as any)?.error || 'Failed to generate logos';
    throw new Error(errMsg);
  },
};
